// Currency configuration and formatting utilities
// Centralized currency formatting for the entire app

let currencySymbol = '$';
let currencyCode = 'USD';

const ZERO_DECIMAL_CURRENCIES = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA',
  'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
]);

/**
 * Configure the currency symbol and code used throughout the app.
 * Call this once at app initialization.
 */
export function configureCurrency(symbol: string, code?: string): void {
  currencySymbol = symbol;
  if (code) currencyCode = code.toUpperCase();
}

/**
 * Get the currently configured currency symbol.
 */
export function getCurrencySymbol(): string {
  return currencySymbol;
}

/**
 * Whether the current currency uses zero decimal places (e.g. JPY, KRW).
 */
function isZeroDecimal(): boolean {
  return ZERO_DECIMAL_CURRENCIES.has(currencyCode);
}

/**
 * Format a number as currency with commas and the configured currency symbol.
 * @param amount - The amount to format (number or string)
 * @returns Formatted string like "$1,234.56" or "¥1,235" for zero-decimal currencies
 */
export function formatAmount(amount: number | string): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return isZeroDecimal() ? `${currencySymbol}0` : `${currencySymbol}0.00`;

  const fractionDigits = isZeroDecimal() ? 0 : 2;
  const formatted = num.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

  return `${currencySymbol}${formatted}`;
}

/**
 * Format a number as currency with dollars and cents separated.
 * Useful for displaying large amounts with smaller cents.
 * @param amount - The amount to format (number or string)
 * @returns Object with { dollars: "$1,234", cents: ".56" } or { dollars: "¥1,235", cents: "" } for zero-decimal
 */
export function formatAmountWithSmallCents(amount: number | string): { dollars: string; cents: string } {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return isZeroDecimal()
    ? { dollars: `${currencySymbol}0`, cents: '' }
    : { dollars: `${currencySymbol}0`, cents: '.00' };

  if (isZeroDecimal()) {
    const formatted = Math.round(num).toLocaleString('en-US');
    return { dollars: `${currencySymbol}${formatted}`, cents: '' };
  }

  const formatted = num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const [dollars, cents] = formatted.split('.');
  return { dollars: `${currencySymbol}${dollars}`, cents: `.${cents}` };
}

/**
 * Format a number as compact currency (k for thousands, M for millions).
 * Useful for chart axes and space-constrained displays.
 * @param amount - The amount to format
 * @returns Formatted string like "$1.2M" or "$450k"
 */
export function formatAmountCompact(amount: number | string): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return `${currencySymbol}0`;

  if (num >= 1000000) {
    return `${currencySymbol}${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${currencySymbol}${(num / 1000).toFixed(1)}k`;
  }
  return formatAmountNoCents(num);
}

/**
 * Format a number as currency without decimal places.
 * @param amount - The amount to format
 * @returns Formatted string like "$1,234"
 */
export function formatAmountNoCents(amount: number | string): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return `${currencySymbol}0`;

  const formatted = num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return `${currencySymbol}${formatted}`;
}
